import { checkoutOptimizationTreatmentCacheId } from "@redotech/redo-conversion-core-common/conversion/treatment-cache";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import { setLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import { CHECKOUT_OPTIMIZATION_TREATMENT_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { useEffect } from "react";
import { StorefrontClient } from "../../client/storefront";
import { useCart } from "../../utils/cart";
import { useTextReplacement } from "./use-text-replacement";

const SPLIT_STOREFRONT_TREATMENT_DURATION_HOURS = 1;

export function useSplitStorefrontFeatures({
  autocheck,
  storefrontExtensionShopifyClient,
}: {
  autocheck: WidgetAutocheck;
  storefrontExtensionShopifyClient: StorefrontClient;
}) {
  // Apply text replacements
  useTextReplacement(autocheck);

  const cart = useCart();

  // Store the treatment ID in localStorage for consistency across page loads
  useEffect(() => {
    if (!autocheck.conversion.enabled) {
      return;
    }

    const treatmentId = autocheck.conversion.storefrontSplitTreatmentId;

    if (treatmentId) {
      setLocalStorageWithExpiry(
        CHECKOUT_OPTIMIZATION_TREATMENT_ID_KEY,
        treatmentId,
        SPLIT_STOREFRONT_TREATMENT_DURATION_HOURS,
      );
    }
  }, [autocheck.conversion]);

  // Store the treatment ID on all the cart's line items as line item properties
  useEffect(() => {
    if (!autocheck.conversion.enabled) {
      return;
    }

    const treatmentId = autocheck.conversion.storefrontSplitTreatmentId;

    if (!treatmentId || !cart?.items || cart.items.length === 0) {
      return;
    }

    const updateLineItems = async () => {
      try {
        if (!storefrontExtensionShopifyClient.updateLineItemAttributes) {
          console.warn("updateLineItemAttributes is not available");
          return;
        }

        // Only update line items that don't already have the treatment ID
        const updates = cart.items
          .filter((lineItem) => {
            const existingProperties = lineItem.properties ?? {};
            return (
              existingProperties[checkoutOptimizationTreatmentCacheId] !==
              treatmentId
            );
          })
          .map((lineItem) => {
            const existingProperties = lineItem.properties ?? {};
            return {
              lineItemId: String(lineItem.id),
              attributes: [
                ...Object.entries(existingProperties).map(([key, value]) => ({
                  key,
                  value,
                })),
                {
                  key: checkoutOptimizationTreatmentCacheId,
                  value: treatmentId,
                },
              ],
            };
          });

        if (updates.length === 0) {
          return;
        }

        await storefrontExtensionShopifyClient.updateLineItemAttributes(
          updates,
        );
      } catch (error) {
        console.error("Error updating line item attributes:", error);
      }
    };

    void updateLineItems();
  }, [autocheck.conversion, cart?.items, storefrontExtensionShopifyClient]);
}
